import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { onConnectivityChange } from "@/lib/api";

const AuthContext = createContext(null);

// user: null = checking, false = anonymous, object = authenticated
// { offline: true } = session not yet verified (API unreachable)
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const bootstrap = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e) {
      if (e.response && (e.response.status === 401 || e.response.status === 403)) {
        // Explicit rejection: no valid session (refresh already attempted in interceptor).
        setUser(false);
      } else {
        // Network/API unreachable: open the authenticated shell in offline mode
        // only if we already had a user; otherwise treat as anonymous.
        setUser((u) => (u && typeof u === "object") ? u : { offline: true });
      }
    } finally {
      setReady(true);
    }
  }, []);

  // Re-validate session (used on reconnect). Never downgrades on a mere network failure.
  const refreshSession = useCallback(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch((e) => {
        if (e.response && (e.response.status === 401 || e.response.status === 403)) setUser(false);
      });
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  useEffect(() => {
    const onOnline = () => refreshSession();
    window.addEventListener("online", onOnline);
    const off = onConnectivityChange((online) => { if (online) refreshSession(); });
    return () => { window.removeEventListener("online", onOnline); off(); };
  }, [refreshSession]);

  const login = async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(false);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
